import { useState } from "react";
import axios from "../axiosConfig";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAdmin } from "../redux/adminSlice";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, LockKeyhole } from "lucide-react";
import logo from "../images/logo.png";
import nutritionBowl from "../images/nutrition_bowl_vibrant.png";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      const response = await axios.post("https://api.dailyfit.ae/api/admin/login", {
        username: email,
        password,
      }, { withCredentials: true });

      if (response.data?.status) {
        console.log("Login Successful:", response.data.message);

        // Save to Redux and LocalStorage (via reducer)
        // Explicitly include email since API might not return it in the body
        const adminData = {
          ...response.data.data, // role, etc.
          token: response.data.token,
          email: email,
          status: true
        };
        // Explicitly update localStorage to ensure persistence
        localStorage.setItem('userInfo', JSON.stringify(adminData));
        dispatch(setAdmin(adminData));
        sessionStorage.setItem("token", response.data.token);

        console.log(response.data.data.role, "The role of user")
        if (response.data.data.role == 1) {
          navigate("/admin-dashboard");
        }
        else if (response.data.data.role == 3) {
          navigate("/delivery");
        }

      } else {
        setError("Login Failed! Check your credentials.");
      }
    } catch (error) {
      console.error("Login Error:", error.response?.data || error.message);
      setError("Login Failed! Check your credentials.");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6 md:p-12 font-sans text-slate-800">
      <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
        
        {/* Left Side: Branding, Title, Description, and Circular Graphic */}
        <div className="flex flex-col items-start max-w-lg">
          {/* Logo container styled with a natural background image theme like in the screenshot */}
          <div className="mb-8 p-3 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.02)] border border-slate-100 flex items-center justify-center">
            <img src={logo} alt="Dailyfit Logo" className="h-10 md:h-12 object-contain" />
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            Elevating <span className="text-[#064e3b]">Nutritional</span> Excellence.
          </h1>

          <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-8">
            Access the administrative core of Dailyfit. Securely manage patient data, nutritional programs, and platform analytics with professional-grade tools.
          </p>

          {/* Circular Graphic preview containing the nutrition bowl */}
          <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full bg-emerald-50/50 flex items-center justify-center p-3 border border-emerald-100 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)]">
            <div className="w-full h-full rounded-full overflow-hidden border-2 border-white shadow-md relative">
              <img
                src={nutritionBowl}
                alt="Nutrition Preview"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              {/* Overlay mask to mimic the preview UI cutout in the screenshot */}
              <div className="absolute inset-0 bg-black/5 flex items-center justify-center">
                <div className="text-[10px] font-bold text-white bg-black/40 px-2.5 py-0.5 rounded-full uppercase tracking-wider backdrop-blur-xs">
                  Portal Preview
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Card */}
        <div className="flex flex-col gap-6 w-full max-w-md justify-self-center md:justify-self-end">
          
          <div className="bg-white rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.03)] border border-slate-100/80 p-8 md:p-10 flex flex-col gap-6">
            
            {/* Header info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-1">
                Welcome Back
              </h2>
              <p className="text-xs text-slate-400 font-semibold">
                Please enter your credentials to proceed.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-xs px-4 py-3 rounded-xl border border-red-100 text-center font-medium">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="flex flex-col gap-5">
              
              {/* Email Input */}
              <div>
                <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-2 uppercase">
                  Admin Email
                </label>
                <div className="relative flex items-center">
                  <Mail className="absolute left-3.5 text-slate-400 h-5 w-5" />
                  <input
                    type="email"
                    className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-slate-700 placeholder:text-slate-300 text-sm transition-all"
                    placeholder="admin@dailyfit.ae"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Password / Security Key Input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-[10px] font-bold text-slate-400 tracking-wider uppercase">
                    Security Key
                  </label>
                  <a
                    href="#forgot"
                    onClick={(e) => {
                      e.preventDefault();
                      alert("Please contact the super admin to reset your security key.");
                    }}
                    className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wider"
                  >
                    Forgot?
                  </a>
                </div>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3.5 text-slate-400 h-5 w-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-11 pr-11 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#064e3b] text-slate-700 placeholder:text-slate-300 text-sm tracking-wide transition-all"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3.5 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Keep me logged in */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="keepLoggedIn"
                  checked={keepLoggedIn}
                  onChange={(e) => setKeepLoggedIn(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 transition-all cursor-pointer"
                />
                <label
                  htmlFor="keepLoggedIn"
                  className="text-xs font-medium text-slate-400 cursor-pointer select-none"
                >
                  Keep me logged in for 30 days
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 font-bold text-white bg-[#064e3b] hover:bg-[#043d2e] rounded-xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98]"
              >
                <span>Sign in to Dashboard</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            {/* Security Badges */}
            <div className="flex justify-center items-center gap-6 border-t border-slate-100 pt-5 text-[10px] font-bold text-slate-400 tracking-wider">
              <div className="flex items-center gap-1.5">
                <LockKeyhole className="h-3.5 w-3.5 text-slate-400" />
                <span>SSL SECURED</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
                <span>AES-256 BIT</span>
              </div>
            </div>

          </div>

          {/* Under card access message */}
          <p className="text-center text-xs text-slate-400 font-medium">
            Unauthorized access is prohibited.{" "}
            <a
              href="#request-access"
              onClick={(e) => {
                e.preventDefault();
                alert("Contact support at admin@dailyfit.ae to request administrative portal credentials.");
              }}
              className="font-bold text-emerald-600 hover:text-emerald-700 hover:underline"
            >
              Request Portal Access
            </a>
          </p>

        </div>

      </div>
    </div>
  );
};

export default AdminLogin;
